"use client";

import { Children, useState, useEffect, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";

// Same Embla carousel the site uses for ExplorerSection (ExplorerSlider) — same
// options, slide breakpoints (1 / 2 / 4) and dot style — so the inventory
// carousel is visually and behaviourally consistent with the rest of the page.
//
// The cards arrive as server-rendered `children` and are only WRAPPED in Embla
// slides, so every card stays in the initial HTML (crawlable); Embla just
// enhances scrolling/drag/dots on the client.
//
// NO AUTOPLAY here (ExplorerSlider, a blog-discovery carousel, still has it at a
// slower cadence). This is a product list: every slide carries its own "Book now"
// CTA, and auto-advancing moved that CTA out from under a reaching thumb. Embla's
// Autoplay only honours `stopOnInteraction` AFTER the first interaction, so the
// mis-tap window was exactly the read-then-tap moment we care most about.
const InventoryCarousel = ({ children }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "start",
    slidesToScroll: 1,
    containScroll: "trimSnaps",
  });

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  const onReInit = useCallback(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    onSelect();
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi) return undefined;
    // Defer the initial snap read to the next frame (after Embla lays out) so we
    // never set state synchronously inside the effect body.
    const raf = requestAnimationFrame(onReInit);
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onReInit);
    return () => {
      cancelAnimationFrame(raf);
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onReInit);
    };
  }, [emblaApi, onSelect, onReInit]);

  const dotsToShow = Math.min(scrollSnaps.length, 3);

  return (
    <div className="explorer-carousel flex-1 mx-[5%] md960:mx-[4%]">
      {/* pt/pb give the card shadow + hover-lift room so overflow-hidden
          doesn't clip them (which read as a "boxed" look / cut-off top). */}
      <div className="overflow-hidden pt-3 pb-6" ref={emblaRef}>
        <div className="flex -ml-[5px] md960:-ml-[15px]">
          {Children.map(children, (child) => (
            <div className="min-w-0 flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_25%] pl-[5px] md960:pl-[15px]">
              {child}
            </div>
          ))}
        </div>
      </div>

      {/* Dots — max 3, matching ExplorerSlider. The row height is reserved
          server-side (min-h) so the post-mount dot render doesn't shift the
          content below it (avoids CLS). */}
      <div className="flex justify-center gap-2 mt-4 min-h-[24px]">
        {scrollSnaps.length > 1
          ? scrollSnaps.slice(0, dotsToShow).map((_, index) => (
              <button
                key={index}
                type="button"
                className="relative w-6 h-6 flex items-center justify-center cursor-pointer bg-transparent"
                onClick={() => emblaApi?.scrollTo(index)}
                aria-label={`Go to slide ${index + 1}`}
              >
                <span
                  className={`block w-2.5 h-2.5 rounded-full ${
                    index === selectedIndex
                      ? "bg-[var(--color-primary)]"
                      : "bg-[var(--color-text-muted)]"
                  }`}
                />
              </button>
            ))
          : null}
      </div>
    </div>
  );
};

export default InventoryCarousel;
